import { ClientProvisioner, ClientProvisionerOptions } from '@journeyapps-platform/lib-journey-core';
import * as accounts from '@journeyapps-platform/sdk-accounts-hub';
import { FlightLogClient } from '@journeyapps-platform/sdk-flight-log';
import { AccountsPortalClient } from '@journeyapps-platform/sdk-accounts';
import { AlphaClient } from '@journeyapps-platform/sdk-alpha';
import { AdminTokenProvisioner } from './AdminTokenProvisioner';
import { ProviderAPIClient } from '@journeyapps-platform/sdk-source-server';
import { AzureDevopsIntrospectionClient } from '@journeyapps-platform/sdk-cerberus';
import { AdminNetworkClient } from './AdminNetworkClient';

export interface AdminClientProvisionerParams extends Omit<ClientProvisionerOptions, 'token_provisioner'> {
  alpha_url: string;
  accounts_portal_url: string;
  accounts_hub_url: string;
  source_service_url: string;
  intercom_url: string;
  deploy_service_url: string;
  flightlog_url: string;
  token_provisioner: AdminTokenProvisioner;
}

export class AdminClientProvisioner extends ClientProvisioner {
  private config: AdminClientProvisionerParams;

  constructor(params: AdminClientProvisionerParams) {
    super(params);
    this.config = params;
  }

  provisionProviderClient() {
    return new ProviderAPIClient({
      endpoint: this.config.source_service_url,
      client: new AdminNetworkClient({
        headers: () => {
          return this.standardHeaders();
        }
      })
    });
  }

  provisionAzureIntegrationClient(): AzureDevopsIntrospectionClient {
    return new AzureDevopsIntrospectionClient({
      endpoint: this.config.cerberus_url,
      client: new AdminNetworkClient({
        headers: () => {
          return this.standardHeaders();
        }
      })
    });
  }

  async provisionSensitiveUserHubClient() {
    return new accounts.SensitiveProfileAccountsV5Client({
      endpoint: this.config.accounts_hub_url,
      client: new AdminNetworkClient({
        headers: {
          Authorization: `Bearer ${await this.config.token_provisioner.sensitiveUserHubToken.getToken()}`
        }
      })
    });
  }

  async getSensitiveAccountsToken() {
    return this.config.token_provisioner.sensitiveUserHubToken.getToken();
  }

  provisionHubClientFromTrialToken(token: string) {
    return new accounts.AccountsV5Client({
      endpoint: this.config.accounts_hub_url,
      client: new AdminNetworkClient({
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
    });
  }

  provisionFlightLogClient(token?: string) {
    return new FlightLogClient({
      endpoint: this.config.flightlog_url,
      client: new AdminNetworkClient({
        headers: token
          ? () => {
              return {
                Authorization: `Bearer ${token}`
              };
            }
          : this.standardHeaders
      })
    });
  }

  provisionSessionAccountsPortalClient() {
    return new AccountsPortalClient({
      endpoint: this.config.accounts_portal_url,
      client: new AdminNetworkClient()
    });
  }

  provisionTokenAccountsPortalClient(token: string) {
    return new AccountsPortalClient({
      endpoint: this.config.accounts_portal_url,
      client: new AdminNetworkClient({
        headers: () => {
          return {
            Authorization: `Bearer ${token}`
          };
        }
      })
    });
  }

  provisionAlphaClient() {
    return new AlphaClient({
      endpoint: this.config.alpha_url,
      client: new AdminNetworkClient({
        headers: () => {
          return this.standardHeaders();
        }
      })
    });
  }
}
